import { Box, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import {
  IconArrowsExchange,
  IconCreditCard,
  IconFileText,
  IconSend,
} from "@tabler/icons-react";

type TimelineStep = {
  title: string;
  done: boolean;
  completedAt: string | Date | null;
};

type TimelineSide = {
  request: TimelineStep;
  payment: TimelineStep;
  nda: TimelineStep;
  partnerReadiness: TimelineStep;
};

export type CollaborationTimelineCardItem = {
  matchId: string;
  gtid: string;
  senderGtid?: string | null;
  receiverGtid?: string | null;
  initiatedAt: string | Date;
  status: string;
  mySide: TimelineSide;
  partnerSide: TimelineSide;
};

type Props = {
  item: CollaborationTimelineCardItem;
};

function formatDate(value: string | Date | null): string {
  if (!value) return "Pending";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Pending";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function StepNode({ step }: { step: TimelineStep }) {
  const done = step.done;
  const nodeColor = done ? "var(--mantine-color-green-6)" : "var(--mantine-color-gray-5)";
  const textColor = done ? "var(--mantine-color-green-7)" : "var(--mantine-color-gray-7)";
  const bg = done ? "var(--mantine-color-green-0)" : "var(--mantine-color-gray-0)";
  const iconAndBorderColor = nodeColor;
  const stepTitle = step.title.toLowerCase();

  const StepIcon = (() => {
    if (stepTitle.includes("payment")) return IconCreditCard;
    if (stepTitle.includes("nda")) return IconFileText;
    if (stepTitle.includes("readiness") || stepTitle.includes("questionnaire")) return IconFileText;
    return IconSend;
  })();

  return (
    <Stack gap={6} align="center" style={{ minWidth: 80 }}>
      <ThemeIcon
        size={36}
        radius="50%"
        variant="light"
        styles={{
          root: {
            borderRadius: "50%",
            backgroundColor: bg,
            color: iconAndBorderColor,
            border: `1px solid ${iconAndBorderColor}`,
          },
        }}
      >
        <StepIcon size={18} />
      </ThemeIcon>
      <Text size="sm" fw={600} c={textColor} ta="center">
        {step.title}
      </Text>
      <Text size="xs" c={done ? "green.7" : "gray.7"} ta="center">
        {formatDate(step.completedAt)}
      </Text>
    </Stack>
  );
}

function MobileStepIcon({ step }: { step: TimelineStep }) {
  const done = step.done;
  const nodeColor = done ? "var(--mantine-color-green-6)" : "var(--mantine-color-gray-5)";
  const bg = done ? "var(--mantine-color-green-0)" : "var(--mantine-color-gray-0)";
  const stepTitle = step.title.toLowerCase();

  const StepIcon = (() => {
    if (stepTitle.includes("payment")) return IconCreditCard;
    if (stepTitle.includes("nda")) return IconFileText;
    if (stepTitle.includes("readiness") || stepTitle.includes("questionnaire")) return IconFileText;
    return IconSend;
  })();

  return (
    <ThemeIcon
      size={34}
      radius="xl"
      variant="light"
      styles={{
        root: {
          backgroundColor: bg,
          color: nodeColor,
          border: `1px solid ${nodeColor}`,
          flexShrink: 0,
        },
      }}
      aria-label={step.title}
      title={`${step.title} • ${done ? `Done (${formatDate(step.completedAt)})` : "Pending"}`}
    >
      <StepIcon size={18} />
    </ThemeIcon>
  );
}

function MobileSide({ title, side, reverse }: { title: string; side: TimelineSide; reverse?: boolean }) {
  const orderedSteps = [side.request, side.payment, side.nda, side.partnerReadiness];
  const steps = reverse ? [...orderedSteps].reverse() : orderedSteps;

  return (
    <Stack gap={6}>
      <Text size="sm" fw={800} c="blue.7">
        {title}
      </Text>
      <Group wrap="nowrap" gap={0} align="center" style={{ width: "100%" }}>
        {steps.map((step, idx) => {
          const hasNext = idx < steps.length - 1;
          const connectorColor = step.done ? "var(--mantine-color-green-6)" : "var(--mantine-color-gray-5)";
          return (
            <Box
              key={`${title}-${step.title}-${idx}`}
              style={{ position: "relative", flex: 1, display: "flex", justifyContent: "center" }}
            >
              <MobileStepIcon step={step} />
              {hasNext ? (
                <Box
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "calc(50% + 17px)",
                    right: "-50%",
                    height: 2,
                    backgroundColor: connectorColor,
                    borderRadius: 999,
                    transform: "translateY(-50%)",
                    opacity: 0.9,
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Group>

      <Group wrap="nowrap" gap={0} align="center" style={{ width: "100%" }}>
        {steps.map((step, idx) => (
          <Box key={`${title}-time-${idx}`} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <Text size="xs" c="dimmed" ta="center" style={{ width: 48, lineHeight: 1.1 }}>
              {step.done ? formatDate(step.completedAt) : "—"}
            </Text>
          </Box>
        ))}
      </Group>
    </Stack>
  );
}

function SideSteps({ title, side, reverse }: { title: string; side: TimelineSide; reverse?: boolean }) {
  const orderedSteps = [side.request, side.payment, side.nda, side.partnerReadiness];
  const steps = reverse ? [...orderedSteps].reverse() : orderedSteps;

  return (
    <Stack gap="xs" style={{ flex: 1 }}>
      <Text size="sm" fw={600} c="blue.7" ta="center">
        {title}
      </Text>
      <Group justify="space-between" wrap="nowrap" gap={4} align="flex-start">
        {steps.map((step, idx) => {
          const hasNext = idx < steps.length - 1;
          const connectorColor = step.done ? "var(--mantine-color-green-6)" : "var(--mantine-color-gray-5)";

          return (
            <Box
              key={`${title}-${step.title}-${idx}`}
              style={{ position: "relative", flex: 1, minWidth: 80, display: "flex", justifyContent: "center" }}
            >
              <StepNode step={step} />
              {hasNext ? (
                <Box
                  style={{
                    position: "absolute",
                    top: 18,
                    left: "calc(50% + 18px)",
                    right: "-50%",
                    height: 2,
                    backgroundColor: connectorColor,
                    borderRadius: 999,
                    opacity: 0.9,
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Group>
    </Stack>
  );
}

export default function CollaborationTimelineCard({ item }: Props) {
  const leftLane = { title: "Your Side", side: item.mySide, reverse: false };
  const rightLane = { title: "Partner Side", side: item.partnerSide, reverse: true };

  return (
    <Paper withBorder radius="md" p="md" style={{ borderColor: "#04305c", backgroundColor: "#ebf0ff" }}>
      <Group justify="space-between" align="flex-start" mb="sm">
        <Group justify="space-between" align="flex-start" style={{ width: "100%" }} wrap="nowrap">
          <Stack gap={2}>
            <Text size="xs" c="#04305c">
              Sender GTID
            </Text>
            <Text fw={700} c="#04305c">
              {item.senderGtid || "GTID"}
            </Text>
          </Stack>

          <Stack gap={2} align="center">
            <Text size="xs" c="#04305c">
              Initiated on: {formatDate(item.initiatedAt)}
            </Text>
          </Stack>

          <Stack gap={2} align="flex-end">
            <Text size="xs" c="#04305c">
              Receiver GTID
            </Text>
            <Text fw={700} c="#04305c">
              {item.receiverGtid || item.gtid || "GTID"}
            </Text>
          </Stack>
        </Group>
      </Group>

      {/* Mobile: stacked, readable timeline */}
      <Box hiddenFrom="sm">
        <Stack gap="md">
          <MobileSide title={leftLane.title} side={leftLane.side} reverse={leftLane.reverse} />
          <Group justify="center" align="center">
            <ThemeIcon size={30} radius="50%" variant="light" color="blue">
              <IconArrowsExchange size={16} />
            </ThemeIcon>
          </Group>
          <MobileSide title={rightLane.title} side={rightLane.side} reverse={rightLane.reverse} />
        </Stack>
      </Box>

      {/* Desktop/tablet: existing horizontal lanes */}
      <Group visibleFrom="sm" wrap="nowrap" align="stretch" gap="md">
        <Box style={{ flex: 1, paddingRight: 18 }}>
          <SideSteps title={leftLane.title} side={leftLane.side} reverse={leftLane.reverse} />
        </Box>
        <Group justify="center" align="center" style={{ minWidth: 48 }}>
          <ThemeIcon size={32} radius="50%" variant="light" color="blue">
            <IconArrowsExchange size={18} />
          </ThemeIcon>
        </Group>
        <Box style={{ flex: 1, paddingLeft: 18 }}>
          <SideSteps title={rightLane.title} side={rightLane.side} reverse={rightLane.reverse} />
        </Box>
      </Group>
    </Paper>
  );
}
